class WebSocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.reconnectTimer = null;
    this.pingInterval = null;
    this.isIntentionallyClosed = false;
  }

  connect(token) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return this.socket;
    }

    this.isIntentionallyClosed = false;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = import.meta.env.VITE_WS_URL || `${wsProtocol}//${window.location.host}`;

    try {
      // Connect with token as query parameter
      this.socket = new WebSocket(`${wsUrl}/ws/dashboard?token=${encodeURIComponent(token)}`);

      this.setupEventListeners();

      return this.socket;
    } catch (error) {
      console.error('WebSocket connection failed:', error);
      throw error;
    }
  }

  setupEventListeners() {
    if (!this.socket) {return;}

    this.socket.onopen = () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
      this.emit('connected');

      // Start ping interval to keep connection alive
      this.startPingInterval();

      // Send initial subscription
      this.sendMessage('subscribe', {
        feeds: ['overview', 'metrics', 'alerts', 'calls']
      });
    };

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Handle different message types
        switch (data.type) {
          case 'welcome':
            this.emit('welcome', data.data);
            break;
          case 'metrics_update':
            this.emit('metrics_update', data.data);
            break;
          case 'alerts_update':
            this.emit('alerts_update', data.data);
            break;
          case 'new_call':
            this.emit('new_call', data.data);
            break;
          case 'call_ended':
            this.emit('call_ended', data.data);
            break;
          case 'new_alert':
            this.emit('new_alert', data.data);
            break;
          case 'overview_update':
            this.emit('overview_update', data.data);
            break;
          case 'pong':
            // Pong received, connection is alive
            break;
          case 'dialer_status':
            this.emit('dialer_status', data.data);
            break;
          default:
            console.log('Unhandled WebSocket message type:', data.type);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    this.socket.onclose = (event) => {
      console.log('WebSocket disconnected:', event.code, event.reason);
      this.stopPingInterval();
      this.emit('disconnected', event.reason);

      // Attempt reconnection if not intentionally closed
      if (!this.isIntentionallyClosed && event.code !== 1000) {
        this.handleReconnect();
      }
    };

    this.socket.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.emit('error', error);
    };
  }

  startPingInterval() {
    // Send ping every 30 seconds to keep connection alive
    this.pingInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.sendMessage('ping');
      }
    }, 30000);
  }

  stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.emit('max_reconnect_attempts_reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      const token = localStorage.getItem('authToken');
      if (token) {
        this.connect(token);
      }
    }, delay);
  }

  sendMessage(type, data = {}) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected, cannot send message');
      return;
    }

    const message = JSON.stringify({
      type,
      data,
      timestamp: new Date().toISOString()
    });

    this.socket.send(message);
  }

  subscribe(feeds) {
    this.sendMessage('subscribe', {
      feeds: Array.isArray(feeds) ? feeds : [feeds]
    });
  }

  unsubscribe(feeds) {
    this.sendMessage('unsubscribe', {
      feeds: Array.isArray(feeds) ? feeds : [feeds]
    });
  }

  getMetrics() {
    this.sendMessage('get_metrics');
  }

  getAlerts() {
    this.sendMessage('get_alerts');
  }

  ping() {
    this.sendMessage('ping');
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in WebSocket listener:', error);
        }
      });
    }
  }

  disconnect() {
    this.isIntentionallyClosed = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.stopPingInterval();

    if (this.socket) {
      this.socket.close(1000, 'Client disconnect');
      this.socket = null;
    }

    this.listeners.clear();
    this.reconnectAttempts = 0;
  }

  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  getConnectionStatus() {
    if (!this.socket) {return 'disconnected';}

    switch (this.socket.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'disconnected';
      default:
        return 'disconnected';
    }
  }
}

// Create singleton instance
const webSocketService = new WebSocketService();

export default webSocketService;